/**
 * Bulk KRA import.
 *
 * CSV, not xlsx. A spreadsheet saved as CSV opens in Excel unchanged and needs no
 * parser dependency on either side; an .xlsx reader would be a megabyte of library
 * to read four columns. The sample template HR downloads is
 * `apps/web/public/kra-import-sample.csv`.
 *
 * The file is FLAT: one row per goal, repeating the KRA name. One KRA with three
 * goals is three rows. That is the shape a spreadsheet is good at - a nested format
 * would need HR to think about indentation in a grid that has none.
 *
 * Parsing here is a courtesy that turns a bad file into per-line messages. The API
 * still validates every KRA it is handed; this never becomes the control.
 */

export interface ImportedGoal {
  name: string;
  description: string | null;
  weightage: number;
}

export interface ImportedKra {
  name: string;
  goals: ImportedGoal[];
}

export interface KraImportResult {
  kras: ImportedKra[];
  /**
   * One message per rejected row, quoting the line number Excel shows in its
   * gutter - a message about "row 3" is useless if row 3 counted from the wrong end.
   */
  errors: string[];
}

export const KRA_IMPORT_HEADERS = ['KRA Name', 'Goal Name', 'Description', 'Weightage'] as const;

/** Mirrors the zod limits in kra-groups.controller.ts, so a long cell fails by line. */
const MAX_KRA_NAME = 120;
const MAX_GOAL_NAME = 200;
const MAX_DESCRIPTION = 2000;

/**
 * RFC 4180 CSV. Quoted fields may hold commas, newlines and `""` escapes - which
 * matters because a goal description is exactly where someone writes
 * "close 3 deals, every quarter".
 */
export function parseCsv(text: string): string[][] {
  // Excel writes a UTF-8 BOM; left in place it corrupts the first header cell.
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;

  for (let i = 0; i < src.length; i += 1) {
    const char = src[i];

    if (quoted) {
      if (char !== '"') {
        field += char;
      } else if (src[i + 1] === '"') {
        field += '"';
        i += 1;
      } else {
        quoted = false;
      }
      continue;
    }

    if (char === '"') quoted = true;
    else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (char !== '\r') field += char;
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/**
 * Group flat rows into KRAs, preserving file order.
 *
 * KRA names are matched case-insensitively so "Software Engineer" and
 * "software engineer" land in one KRA rather than two near-identical ones nobody
 * notices until a picker shows both.
 */
export function parseKraCsv(text: string): KraImportResult {
  const rows = parseCsv(text);
  const errors: string[] = [];
  const byKey = new Map<string, ImportedKra>();
  const order: string[] = [];

  // Skip the header only if the file has one. A sheet pasted without it still
  // imports, instead of silently dropping its first goal.
  const hasHeader = rows[0]?.[0]?.trim().toLowerCase() === 'kra name';
  const body = hasHeader ? rows.slice(1) : rows;
  const firstLine = hasHeader ? 2 : 1;

  body.forEach((cells, index) => {
    const line = firstLine + index;
    const [kraName = '', goalName = '', description = '', weightage = ''] = cells.map((c) =>
      c.trim(),
    );

    if (kraName === '' && goalName === '' && description === '' && weightage === '') return;

    if (kraName === '') {
      errors.push(`Line ${line}: KRA Name is required.`);
      return;
    }
    if (goalName === '') {
      errors.push(`Line ${line}: Goal Name is required.`);
      return;
    }
    if (
      kraName.length > MAX_KRA_NAME ||
      goalName.length > MAX_GOAL_NAME ||
      description.length > MAX_DESCRIPTION
    ) {
      errors.push(
        `Line ${line}: too long — KRA Name ${MAX_KRA_NAME}, Goal Name ${MAX_GOAL_NAME}, Description ${MAX_DESCRIPTION} characters at most.`,
      );
      return;
    }

    // Whole numbers only, the same reason Goal.weightage is: three goals at 33.33
    // total 99.99 and would fail an exact-100 check for no reason HR can fix.
    const weight = weightage === '' ? 0 : Number(weightage);
    if (!Number.isInteger(weight) || weight < 0 || weight > 100) {
      errors.push(`Line ${line}: Weightage must be a whole number from 0 to 100.`);
      return;
    }

    const key = kraName.toLowerCase();
    let kra = byKey.get(key);
    if (!kra) {
      kra = { name: kraName, goals: [] };
      byKey.set(key, kra);
      order.push(key);
    }
    kra.goals.push({ name: goalName, description: description || null, weightage: weight });
  });

  return { kras: order.map((key) => byKey.get(key)!), errors };
}

// ---------------------------------------------------------------------------
// Groups (KraSet)
// ---------------------------------------------------------------------------

/**
 * Bulk group import.
 *
 * A group is one level up from a KRA: a named weighting of KRAs that already exist
 * in the library. So the sheet REFERENCES KRAs by name and never creates them - a
 * group sheet that quietly authored the KRAs it mentions would fill the library with
 * empty stencils that rate nothing, which is the same reason "create all
 * designations" leaves them out.
 *
 * Same flat shape as the KRA sheet: one row per KRA, repeating the group name. Three
 * KRAs in one group is three rows.
 */
export interface ImportedKraSetItem {
  /** Matched to the library case-insensitively by the caller, never by id. */
  kraName: string;
  weightage: number;
}

export interface ImportedKraSet {
  name: string;
  items: ImportedKraSetItem[];
}

export interface KraSetImportResult {
  sets: ImportedKraSet[];
  errors: string[];
}

export const KRA_SET_IMPORT_HEADERS = ['Group Name', 'KRA Name', 'Weightage'] as const;

/** Mirrors the zod limit in kra-sets.controller.ts. */
const MAX_SET_NAME = 120;

export function parseKraSetCsv(text: string): KraSetImportResult {
  const rows = parseCsv(text);
  const errors: string[] = [];
  const byKey = new Map<string, ImportedKraSet>();
  const order: string[] = [];

  const hasHeader = rows[0]?.[0]?.trim().toLowerCase() === 'group name';
  const body = hasHeader ? rows.slice(1) : rows;
  const firstLine = hasHeader ? 2 : 1;

  body.forEach((cells, index) => {
    const line = firstLine + index;
    const [setName = '', kraName = '', weightage = ''] = cells.map((c) => c.trim());

    if (setName === '' && kraName === '' && weightage === '') return;

    if (setName === '') {
      errors.push(`Line ${line}: Group Name is required.`);
      return;
    }
    if (kraName === '') {
      errors.push(`Line ${line}: KRA Name is required.`);
      return;
    }
    if (setName.length > MAX_SET_NAME || kraName.length > MAX_KRA_NAME) {
      errors.push(`Line ${line}: too long — ${MAX_SET_NAME} characters at most.`);
      return;
    }

    /**
     * Required, and from 1 - unlike a goal's weightage, which may be left blank.
     * A group's shares are the exact-100 budget, so a blank or a zero is a row that
     * cannot contribute and would only fail later as "5% missing" with nothing on
     * screen pointing at the line that caused it. → validateKraSet
     */
    const weight = Number(weightage);
    if (weightage === '' || !Number.isInteger(weight) || weight < 1 || weight > 100) {
      errors.push(`Line ${line}: Weightage must be a whole number from 1 to 100.`);
      return;
    }

    const key = setName.toLowerCase();
    let set = byKey.get(key);
    if (!set) {
      set = { name: setName, items: [] };
      byKey.set(key, set);
      order.push(key);
    }

    // The same KRA twice at 25+25 is a data-entry slip, not a 50% weighting - the
    // same rule the @@unique on kra_set_items enforces, caught here by line number.
    if (set.items.some((i) => i.kraName.toLowerCase() === kraName.toLowerCase())) {
      errors.push(`Line ${line}: "${kraName}" is already in "${set.name}" — give it one row, not two.`);
      return;
    }

    set.items.push({ kraName, weightage: weight });
  });

  return { sets: order.map((key) => byKey.get(key)!), errors };
}
